import type * as geojson from 'geojson';

import {SWEAnalysisResponse} from './analysis';
import {SLOW_API_ROOT, makeApiRequest} from './api';
import {UnitSystem} from './series';

export type SiteSynopsis = {
  forecastVolumes: Record<string, number | null>;
  ltaVolume: number | null;
  percentOfNormal: Record<string, number | null>;
  /** On aggregated entries: sites whose forecast and LTA cover the whole period */
  siteCount?: number;
};

export type RegionalTimeSeries = {
  timestamps: string[];
  forecast: Record<string, number[]>;
  lta: Record<string, number[]>;
  nwrfc: Record<string, (number | null)[]>;
  /** HF forecast subtotal for only the sites that have NWRFC data (for apples-to-apples comparison) */
  nwrfcMatchedForecast: Record<string, number[]>;
  /** LTA subtotal for only the sites that have NWRFC data */
  nwrfcMatchedLta: number[];
  nwrfcSiteCount: number;
  normalizedRatio: (number | null)[];
  variables: Record<string, {unit: string}>;
};

export type NDSIBlock = {
  observed: {timestamps: string[]; values: (number | null)[]};
  historical: {timestamps: string[]; data: Record<string, (number | null)[]>};
};

export type PerSiteTimeSeries = {
  timestamps: string[];
  forecast: Record<string, number[]>;
  lta: Record<string, number[]>;
  nwrfc: Record<string, (number | null)[]>;
  initializationTime: string;
  ndsi?: NDSIBlock;
};

export type EvolutionBoxplotStat = {
  date: string; // "MM/DD"
  initTime?: string;
  q05: number;
  q25: number;
  median: number;
  q75: number;
  q95: number;
};

export type ForecastEvolution = {
  targetPeriod: string;
  unit: string;
  siteName?: string;
  hf: EvolutionBoxplotStat[];
  nwrfc: EvolutionBoxplotStat[];
  observedToDate: Record<string, number>;
  observedDailySeries?: {dates: string[]; cumulativeMaf: number[]};
};

export type GefsChannelOutlook = {
  values: (number | null)[];
  climatology: (number | null)[];
  anomaly: (number | null)[];
  unit: string;
};

export type WeeklyStat = {
  mean: number | null;
  p10: number | null;
  p90: number | null;
  normal: number | null;
};

export type WeeklyOutlookBlock = {
  start: string;
  end: string;
  dayCount: number;
  /** Completed UTC days of the block before the report's run date */
  elapsedDays: number;
  /** Weekly total */
  precipitation: WeeklyStat;
  /** Weekly mean */
  temperature: WeeklyStat;
};

export type WeeklyUnits = {precipitation: string; temperature: string};

export type GefsOutlook = {
  siteName: string;
  issueDate: string;
  horizonDays: number;
  basinAveraged: boolean;
  timestamps: string[];
  precipitation: GefsChannelOutlook;
  temperature: GefsChannelOutlook;
  /** First and last year of the GEFS analysis the climatology averages */
  climatologyYears?: [number, number];
  asOf?: string;
  members?: number;
  weekly?: WeeklyOutlookBlock[];
  weeklyUnits?: WeeklyUnits;
};

export type EcmwfOutlook = {
  siteName: string;
  issueDate: string;
  model: string;
  members: number;
  firstDay: string;
  lastDay: string;
  asOf: string;
  climatologyYears: [number, number];
  weekly: WeeklyOutlookBlock[];
  weeklyUnits: WeeklyUnits;
  exploratory: boolean;
};

export type PrecipToDate = {
  siteName: string;
  waterYear: number;
  start: string;
  cutoff: string;
  unit: string;
  /** Water years (ending year) the history covers; dropped years are absent */
  historyWaterYears: number[];
  /** x positions of every series, in days since `start` */
  daysSinceStart: number[];
  current: (number | null)[];
  /** Null when too few past years have complete data */
  history: {
    mean: (number | null)[];
    median: (number | null)[];
    p10: (number | null)[];
    p90: (number | null)[];
  } | null;
  currentTotal: number;
  meanTotal: number | null;
  medianTotal: number | null;
  /** 1 = driest of the years compared, this year included */
  rank: number | null;
  tiedYears?: number;
  yearsCompared: number;
};

export type CpcCategorical = {
  aboveNormal: number;
  nearNormal: number;
  belowNormal: number;
  signedProbability?: number;
};

export type CpcOutlook = {
  sourceUrl: string;
  issueDate: string | null;
  validPeriod: string | null;
  temperature: CpcCategorical | null;
  precipitation: CpcCategorical | null;
};

export type RegionalReportResponse = {
  projectId: string;
  basinLabel: string;
  reportDate: string;
  forecastYear: number;
  ltaReferencePeriod?: {cutoffYear: number};
  sites: Array<{siteId: string; name: string; lat: number | null; lon: number | null}>;
  targetPeriods: Array<{
    label: string;
    startDate: string;
    endDate: string;
    isPartial: boolean;
    primary?: boolean;
  }>;
  exceedanceProbabilityLabels: Record<string, string>;
  synopsis: Record<string, Record<string, SiteSynopsis>>;
  aggregated: Record<string, SiteSynopsis>;
  timeSeries: RegionalTimeSeries;
  perSiteTimeSeries: Record<string, PerSiteTimeSeries>;
  perSiteSWE: Record<
    string,
    | (SWEAnalysisResponse & {dataSource?: string})
    | {dataSource: 'unavailable'; reason: string; spatialCoverage?: number}
  >;
  forecastEvolution: ForecastEvolution;
  gefsOutlook: GefsOutlook | null;
  ecmwfOutlook?: EcmwfOutlook | null;
  precipToDate?: PrecipToDate | null;
  cpcOutlook: CpcOutlook | null;
  basinOutline: geojson.Geometry | null;
  variables: Record<string, {unit: string}>;
  siteCount: number;
  sitesIncluded: number;
};

export async function fetchRegionalReport(
  projectId: string,
  unitSystem: UnitSystem
): Promise<RegionalReportResponse> {
  // Routed through the slow API (300s timeout) rather than the default
  // api-server, which times out on the long-running regional report request
  // (502 Bad Gateway). getApiRoute detects the SLOW_API_ROOT prefix.
  //
  // The endpoint streams whitespace heartbeats while it builds the report (to
  // stay under Cloudflare's ~100s origin timeout), then sends the JSON body at
  // the end. makeApiRequest reads the full body and JSON.parses it, so leading
  // whitespace is ignored and no special stream handling is needed here. On a
  // mid-stream failure the body is {error} with a 200 status, so guard on a
  // missing data field too.
  const response = await makeApiRequest<RegionalReportResponse>(
    `${SLOW_API_ROOT}/api/internal/regional_report`,
    null,
    {
      method: 'POST',
      body: JSON.stringify({projectId, unitSystem}),
    },
    {preserveBodyError: true}
  );
  if (response.error) {
    throw new Error(response.error);
  }
  if (!response.data) {
    throw new Error('The regional report could not be generated.');
  }
  return response.data;
}
